import type { ITrashedCaseItem } from '@escloud/types';
import { Trash2, AlertCircle, ChevronLeft, ChevronRight, RotateCcw, X } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { ConfirmationDialog } from '@/components/confirmation-dialog';
import { Page, PageBody } from '@/components/layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useTenant } from '@/context';
import { useCurrentTenant } from '@/context/tenant-registry-context';
import { useTrashedCases, useRestoreCase, usePermanentlyDeleteCase } from '@/hooks';
import { ApiError } from '@/services/api-client';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = 10;

export function TrashPage() {
    const { tenantId } = useTenant();
    const { entry: currentTenant, isConfirmed } = useCurrentTenant();
    // Confirmed by a regional API, never by a token claim.
    const isAdmin = isConfirmed && (currentTenant?.role === 'admin' || currentTenant?.role === 'owner');

    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [currentPage, setCurrentPage] = useState(0);

    // Single case operation handling (restore, delete)
    const [selectedCase, setSelectedCase] = useState<ITrashedCaseItem | null>(null);
    const [restoreSingleDialogOpen, setRestoreSingleDialogOpen] = useState(false);
    const [deleteSingleDialogOpen, setDeleteSingleDialogOpen] = useState(false);
    const [singleOperationPending, setSingleOperationPending] = useState(false);

    // Bulk operations handling
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Dialog states
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [bulkOperationPending, setBulkOperationPending] = useState(false);

    const queryParams = useMemo(
        () => ({
            limit: pageSize,
            offset: currentPage * pageSize,
        }),
        [pageSize, currentPage]
    );

    const { data: trashData, isLoading, error } = useTrashedCases(queryParams);
    const restoreCase = useRestoreCase();
    const permanentlyDeleteCase = usePermanentlyDeleteCase();

    const totalPages = trashData ? Math.ceil(trashData.total / pageSize) : 0;

    const handlePageSizeChange = (value: string) => {
        setPageSize(Number(value));
        setCurrentPage(0);
        setSelectedIds(new Set());
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        setSelectedIds(new Set());
    };

    // Selection handlers
    const handleSelectAll = (checked: boolean) => {
        if (checked && trashData) {
            setSelectedIds(new Set(trashData.items.map((item) => item.id)));
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleSelectOne = (id: string, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    };

    const isAllSelected = trashData?.items.length
        ? trashData.items.every((item) => selectedIds.has(item.id))
        : false;

    const isSomeSelected = selectedIds.size > 0;

    // Bulk operations
    const handleBulkRestore = async () => {
        setBulkOperationPending(true);
        try {
            const results = await Promise.allSettled(
                Array.from(selectedIds).map((id) => restoreCase.mutateAsync(id))
            );
            const succeeded = results.filter((r) => r.status === 'fulfilled').length;
            const failed = results.filter((r) => r.status === 'rejected').length;

            if (succeeded > 0) {
                toast.success(`Restored ${succeeded} case${succeeded !== 1 ? 's' : ''}`);
            }
            if (failed > 0) {
                toast.warning(`Failed to restore ${failed} case${failed !== 1 ? 's' : ''}`);
            }
            setSelectedIds(new Set());
            setRestoreDialogOpen(false);
        } catch {
            toast.error('Failed to restore cases');
        } finally {
            setBulkOperationPending(false);
        }
    };

    const handleBulkPermanentDelete = async () => {
        setBulkOperationPending(true);
        try {
            const results = await Promise.allSettled(
                Array.from(selectedIds).map((id) => permanentlyDeleteCase.mutateAsync(id))
            );
            const succeeded = results.filter((r) => r.status === 'fulfilled').length;
            const failed = results.filter((r) => r.status === 'rejected').length;

            if (succeeded > 0) {
                toast.success(`Permanently deleted ${succeeded} case${succeeded !== 1 ? 's' : ''}`);
            }
            if (failed > 0) {
                toast.warning(`Failed to delete ${failed} case${failed !== 1 ? 's' : ''}`);
            }
            setSelectedIds(new Set());
            setDeleteDialogOpen(false);
        } catch {
            toast.error('Failed to delete cases');
        } finally {
            setBulkOperationPending(false);
        }
    };

    // Single item operations (from row actions)
    const handleRestoreSingle = (caseItem: ITrashedCaseItem) => {
        setSelectedCase(caseItem);
        setRestoreSingleDialogOpen(true);
    };

    const handlePermanentDeleteSingle = (caseItem: ITrashedCaseItem) => {
        setSelectedCase(caseItem);
        setDeleteSingleDialogOpen(true);
    };

    const handleConfirmSingleDelete = async () => {
        if (!selectedCase) return;

        setSingleOperationPending(true);
        try {
            await permanentlyDeleteCase.mutateAsync(selectedCase.id);
            toast.success(`Case "${selectedCase.name}" permanently deleted`);
            setSelectedIds((prev) => {
                const next = new Set(prev);
                next.delete(selectedCase.id);
                return next;
            });
        } catch (error) {
            if (error instanceof ApiError) {
                toast.error(error.message || 'Failed to delete case');
            } else {
                toast.error('Failed to delete case');
            }
        } finally {
            setSelectedCase(null);
            setDeleteSingleDialogOpen(false);
            setSingleOperationPending(false);
        }
    };

    const handleConfirmSingleRestore = async () => {
        if (!selectedCase) return;

        setSingleOperationPending(true);
        try {
            await restoreCase.mutateAsync(selectedCase.id);
            toast.success(`Case "${selectedCase.name}" restored`);
            setSelectedIds((prev) => {
                const next = new Set(prev);
                next.delete(selectedCase.id);
                return next;
            });
        } catch (error) {
            if (error instanceof ApiError) {
                toast.error(error.message || 'Failed to restore case');
            } else {
                toast.error('Failed to restore case');
            }
        } finally {
            setSelectedCase(null);
            setRestoreSingleDialogOpen(false);
            setSingleOperationPending(false);
        }
    };

    return (
        <Page>
            <PageBody scroll="y">
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold">Trash</h1>
                            <p className="text-muted-foreground">
                                Deleted cases are kept for 30 days before permanent deletion
                            </p>
                        </div>
                        <Button variant="outline" asChild>
                            <Link to={`/tenants/${tenantId}`}>Back to Cases</Link>
                        </Button>
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle>Deleted Cases</CardTitle>
                            <CardDescription>Cases that have been moved to trash</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Bulk action bar */}
                            {isSomeSelected && (
                                <div className="flex items-center gap-4 rounded-lg border bg-muted/50 p-3">
                                    <span className="text-sm font-medium">{selectedIds.size} selected</span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setRestoreDialogOpen(true)}
                                    >
                                        <RotateCcw className="mr-2 h-4 w-4" />
                                        Restore
                                    </Button>
                                    {isAdmin && (
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => setDeleteDialogOpen(true)}
                                        >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            Delete Permanently
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setSelectedIds(new Set())}
                                    >
                                        <X className="mr-2 h-4 w-4" />
                                        Clear
                                    </Button>
                                </div>
                            )}

                            {/* Content */}
                            {isLoading ? (
                                <TrashTableSkeleton />
                            ) : error ? (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                                    <h3 className="text-lg font-medium">Failed to load trash</h3>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Please try again later
                                    </p>
                                </div>
                            ) : !trashData?.items.length ? (
                                <EmptyState />
                            ) : (
                                <div className="rounded-md border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-12">
                                                    <Checkbox
                                                        checked={isAllSelected}
                                                        onCheckedChange={handleSelectAll}
                                                        aria-label="Select all"
                                                    />
                                                </TableHead>
                                                <TableHead>Case Name</TableHead>
                                                <TableHead>Patient</TableHead>
                                                <TableHead>Deleted By</TableHead>
                                                <TableHead>Deleted At</TableHead>
                                                <TableHead>Days Until Deletion</TableHead>
                                                <TableHead className="w-28"></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {trashData.items.map((item) => (
                                                <TableRow
                                                    key={item.id}
                                                    data-state={
                                                        selectedIds.has(item.id) ? 'selected' : undefined
                                                    }
                                                >
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={selectedIds.has(item.id)}
                                                            onCheckedChange={(checked) =>
                                                                handleSelectOne(item.id, !!checked)
                                                            }
                                                            aria-label={`Select ${item.name}`}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="font-medium">{item.name}</TableCell>
                                                    <TableCell>
                                                        <PatientInfo caseItem={item} />
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.deletedBy.name || item.deletedBy.email}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {formatDate(item.deletedAt)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <DaysRemainingBadge
                                                            days={item.daysUntilPermanentDeletion}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleRestoreSingle(item)}
                                                                disabled={restoreCase.isPending}
                                                            >
                                                                <RotateCcw className="h-4 w-4" />
                                                            </Button>
                                                            {isAdmin && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-destructive hover:text-destructive"
                                                                    onClick={() =>
                                                                        handlePermanentDeleteSingle(item)
                                                                    }
                                                                    disabled={permanentlyDeleteCase.isPending}
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}

                            {/* Pagination controls */}
                            {trashData && trashData.total > 0 && (
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="text-sm text-muted-foreground">
                                        Showing {currentPage * pageSize + 1}-
                                        {Math.min((currentPage + 1) * pageSize, trashData.total)} of{' '}
                                        {trashData.total} deleted cases
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">
                                                Rows per page:
                                            </span>
                                            <Select
                                                value={pageSize.toString()}
                                                onValueChange={handlePageSizeChange}
                                            >
                                                <SelectTrigger className="w-[70px]">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {PAGE_SIZE_OPTIONS.map((size) => (
                                                        <SelectItem key={size} value={size.toString()}>
                                                            {size}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 0}
                                            >
                                                <ChevronLeft className="h-4 w-4" />
                                            </Button>
                                            <span className="min-w-[100px] text-center text-sm">
                                                Page {currentPage + 1} of {totalPages}
                                            </span>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage >= totalPages - 1}
                                            >
                                                <ChevronRight className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Bulk restore confirmation dialog */}
                    <ConfirmationDialog
                        open={restoreDialogOpen}
                        onOpenChange={setRestoreDialogOpen}
                        title="Restore cases"
                        description={`Are you sure you want to restore ${selectedIds.size} selected case${selectedIds.size !== 1 ? 's' : ''} ? They will be moved back to your active cases.`}
                        confirmLabel="Restore"
                        cancelLabel="Cancel"
                        variant="default"
                        onConfirm={handleBulkRestore}
                        isPending={bulkOperationPending}
                    />

                    {/* Bulk permanent delete confirmation dialog */}
                    <ConfirmationDialog
                        open={deleteDialogOpen}
                        onOpenChange={setDeleteDialogOpen}
                        title="Permanently delete cases"
                        description={`Are you sure you want to permanently delete ${selectedIds.size} selected case${selectedIds.size !== 1 ? 's' : ''} ? This action cannot be undone.`}
                        confirmLabel="Delete Permanently"
                        cancelLabel="Cancel"
                        variant="destructive"
                        onConfirm={handleBulkPermanentDelete}
                        isPending={bulkOperationPending}
                    />

                    {/* Single restore confirmation dialog */}
                    <ConfirmationDialog
                        open={restoreSingleDialogOpen}
                        onOpenChange={(open) => {
                            setRestoreSingleDialogOpen(open);
                            if (!open) setSelectedCase(null); // Clear selected case when closing dialog
                        }}
                        title="Restore case"
                        description={`Are you sure you want to restore case ${selectedCase?.name} ? It will be moved back to your active cases.`}
                        confirmLabel="Restore"
                        cancelLabel="Cancel"
                        variant="default"
                        onConfirm={handleConfirmSingleRestore}
                        isPending={singleOperationPending}
                    />

                    {/* Single permanent delete confirmation dialog */}
                    <ConfirmationDialog
                        open={deleteSingleDialogOpen}
                        onOpenChange={(open) => {
                            setDeleteSingleDialogOpen(open);
                            if (!open) setSelectedCase(null); // Clear selected case when closing dialog
                        }}
                        title="Permanently delete case"
                        description={`Are you sure you want to permanently delete case ${selectedCase?.name} ? This action cannot be undone.`}
                        confirmLabel="Delete Permanently"
                        cancelLabel="Cancel"
                        variant="destructive"
                        onConfirm={handleConfirmSingleDelete}
                        isPending={singleOperationPending}
                    />
                </div>
            </PageBody>
        </Page>
    );
}

function PatientInfo({ caseItem }: { caseItem: ITrashedCaseItem }) {
    const parts = [caseItem.lastname, caseItem.firstname].filter(Boolean);
    if (parts.length === 0) {
        return <span className="text-muted-foreground">-</span>;
    }
    return <span>{parts.join(', ')}</span>;
}

function DaysRemainingBadge({ days }: { days: number }) {
    if (days <= 3) {
        return (
            <Badge variant="destructive">
                {days} day{days !== 1 ? 's' : ''}
            </Badge>
        );
    }
    if (days <= 7) {
        return (
            <Badge
                variant="secondary"
                className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
            >
                {days} days
            </Badge>
        );
    }
    return <Badge variant="secondary">{days} days</Badge>;
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
            <Trash2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Trash is empty</h3>
            <p className="text-sm text-muted-foreground mt-1">Deleted cases will appear here</p>
        </div>
    );
}

function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

function TrashTableSkeleton() {
    return (
        <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
            ))}
        </div>
    );
}
